import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { slugify, type Product, type StockMovement } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ProductStatusBadge } from "../components/ProductStatusBadge";
import { ProductMovements } from "../components/ProductMovements";
import { StockAdjustModal } from "../components/StockAdjustModal";
import { MediaInput } from "../components/fields/MediaInput";
import { centsToInput, inputToCents } from "../lib/money";

/** Parse a non-negative whole number from an input, or null if invalid. */
function parseCount(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

export function ProductEditor() {
  const api = useApi();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const { data, loading, error, reload } = useAsync<Product | null>(
    () => (id ? api.getProduct(id) : Promise.resolve(null)),
    [api, id],
  );

  const [product, setProduct] = useState<Product | null>(null);
  const [hydrated, setHydrated] = useState(false);

  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [cartEnabled, setCartEnabled] = useState(true);
  const [lowStockStr, setLowStockStr] = useState("0");
  const [initialQtyStr, setInitialQtyStr] = useState("0");
  const [imageId, setImageId] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [galleryImageIds, setGalleryImageIds] = useState<string[]>([]);

  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [adjusting, setAdjusting] = useState(false);
  const [toUnpublish, setToUnpublish] = useState(false);
  const [movementsToken, setMovementsToken] = useState(0);

  // Hydrate editable state once, after the product loads (edit mode).
  useEffect(() => {
    if (hydrated) return;
    if (id && !data) return; // still loading an existing product
    setProduct(data ?? null);
    if (data) {
      setSku(data.sku);
      setName(data.name);
      setSlug(data.slug);
      setSlugTouched(true);
      setDescription(data.description);
      setPriceInput(centsToInput(data.priceCents));
      setCartEnabled(data.cartEnabled !== false);
      setLowStockStr(String(data.lowStockThreshold));
      setImageId(data.imageId);
      setBody(data.body);
      setGalleryImageIds(data.galleryImageIds);
    }
    setHydrated(true);
  }, [data, id, hydrated]);

  // Auto-suggest the slug from the name for new products.
  useEffect(() => {
    if (product || slugTouched) return;
    setSlug(name.trim() ? slugify(name) : "");
  }, [name, product, slugTouched]);

  const priceCents = inputToCents(priceInput);
  const lowStockThreshold = parseCount(lowStockStr);
  const initialQuantity = parseCount(initialQtyStr);

  const errors = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    if (!name.trim()) out.name = "Name is required";
    if (!sku.trim()) out.sku = "SKU is required";
    if (priceCents === null) out.price = "Enter a valid price (e.g. 19.99)";
    if (lowStockThreshold === null) out.lowStock = "Enter a whole number (0 or more)";
    if (!product && initialQuantity === null) out.initialQty = "Enter a whole number (0 or more)";
    return out;
  }, [name, sku, priceCents, lowStockThreshold, initialQuantity, product]);

  const save = async () => {
    setShowErrors(true);
    setActionError(undefined);
    if (Object.keys(errors).length > 0) return;

    const base = {
      sku: sku.trim(),
      slug: slug.trim() ? slug.trim() : undefined,
      name: name.trim(),
      description,
      priceCents: priceCents as number,
      cartEnabled,
      lowStockThreshold: lowStockThreshold as number,
      imageId,
      body,
      galleryImageIds,
    };

    setSaving(true);
    try {
      if (product) {
        const updated = await api.updateProduct(product.id, base);
        setProduct(updated);
        setPriceInput(centsToInput(updated.priceCents));
        setLowStockStr(String(updated.lowStockThreshold));
      } else {
        const created = await api.createProduct({
          ...base,
          initialQuantity: initialQuantity as number,
        });
        navigate(`/products/${created.id}/edit`, { replace: true });
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!product) return;
    setActing(true);
    setActionError(undefined);
    try {
      setProduct(await api.publishProduct(product.id));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Publish failed.");
    } finally {
      setActing(false);
    }
  };

  const confirmUnpublish = async () => {
    if (!product) return;
    setActing(true);
    setActionError(undefined);
    try {
      setProduct(await api.unpublishProduct(product.id));
      setToUnpublish(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unpublish failed.");
    } finally {
      setActing(false);
    }
  };

  const onAdjusted = (result: { product: Product; movement: StockMovement }) => {
    setProduct(result.product);
    setAdjusting(false);
    setMovementsToken((n) => n + 1);
  };

  if (loading) return <Spinner label="Loading…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">{product ? "Edit product" : "New product"}</h1>
          <p className="page__subtitle page__subtitle--badges">
            {product ? (
              <ProductStatusBadge status={product.status} />
            ) : (
              <span className="muted">Not saved yet</span>
            )}
          </p>
        </div>
        <div className="page__actions">
          <Link to="/products" className="btn btn--ghost btn--sm">
            Back to inventory
          </Link>
        </div>
      </div>

      {actionError ? <ErrorBanner title="Action failed" message={actionError} /> : null}
      {showErrors && Object.keys(errors).length > 0 ? (
        <ErrorBanner
          title="Please fix the highlighted fields"
          message="Some fields need a valid value before saving."
        />
      ) : null}

      <form
        className="entry-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <section className="panel">
          <div className="form-field">
            <label className="form-field__label" htmlFor="product-name">
              Name<span className="required-mark"> *</span>
            </label>
            <input
              id="product-name"
              className={showErrors && errors.name ? "input input--error" : "input"}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {showErrors && errors.name ? (
              <span className="form-field__error">{errors.name}</span>
            ) : null}
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="product-slug">
              Slug <span className="muted">(optional — generated from the name if blank)</span>
            </label>
            <input
              id="product-slug"
              className="input"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
            />
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="product-sku">
              SKU<span className="required-mark"> *</span>
            </label>
            <input
              id="product-sku"
              className={showErrors && errors.sku ? "input input--error" : "input"}
              value={sku}
              onChange={(e) => setSku(e.target.value)}
            />
            {showErrors && errors.sku ? (
              <span className="form-field__error">{errors.sku}</span>
            ) : null}
          </div>

          <div className="form-grid">
            <div className="form-field">
              <label className="form-field__label" htmlFor="product-price">
                Price (AUD)<span className="required-mark"> *</span>
              </label>
              <input
                id="product-price"
                className={showErrors && errors.price ? "input input--error" : "input"}
                inputMode="decimal"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                placeholder="19.99"
              />
              {showErrors && errors.price ? (
                <span className="form-field__error">{errors.price}</span>
              ) : null}
            </div>

            <div className="form-field">
              <label className="form-field__label" htmlFor="product-low-stock">
                Low-stock threshold
              </label>
              <input
                id="product-low-stock"
                className={showErrors && errors.lowStock ? "input input--error" : "input"}
                type="number"
                min={0}
                step={1}
                value={lowStockStr}
                onChange={(e) => setLowStockStr(e.target.value)}
              />
              {showErrors && errors.lowStock ? (
                <span className="form-field__error">{errors.lowStock}</span>
              ) : null}
            </div>
          </div>

          <div className="form-field form-field--check">
            <label className="checkbox-row" htmlFor="product-cart-enabled">
              <input
                id="product-cart-enabled"
                type="checkbox"
                checked={cartEnabled}
                onChange={(e) => setCartEnabled(e.target.checked)}
                aria-describedby="product-cart-enabled-hint"
              />
              Allow adding to cart
            </label>
            <span id="product-cart-enabled-hint" className="form-hint muted">
              Turn off to keep the product visible without accepting website orders.
              Save to update the storefront. Stock is managed separately.
            </span>
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="product-description">
              Description
            </label>
            <textarea
              id="product-description"
              className="input textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={5000}
            />
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="product-image">
              Image
            </label>
            <MediaInput
              id="product-image"
              multiple={false}
              value={imageId ?? undefined}
              onChange={(value) => setImageId(typeof value === "string" ? value : null)}
            />
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="product-body">
              Product page body <span className="muted">(HTML)</span>
            </label>
            <textarea
              id="product-body"
              className="input textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              maxLength={20000}
            />
            <span className="form-hint muted">
              Rich HTML shown on the product page below the description.
            </span>
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="product-gallery">
              Gallery images
            </label>
            <MediaInput
              id="product-gallery"
              multiple
              value={galleryImageIds}
              onChange={(value) =>
                setGalleryImageIds(Array.isArray(value) ? (value as string[]) : [])
              }
            />
          </div>

          {product ? (
            <div className="form-field">
              <label className="form-field__label">On-hand quantity</label>
              <p className="page__subtitle--badges">
                <strong>{product.quantity}</strong>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => setAdjusting(true)}
                >
                  Adjust stock
                </button>
              </p>
            </div>
          ) : (
            <div className="form-field">
              <label className="form-field__label" htmlFor="product-initial-qty">
                Initial quantity
              </label>
              <input
                id="product-initial-qty"
                className={showErrors && errors.initialQty ? "input input--error" : "input"}
                type="number"
                min={0}
                step={1}
                value={initialQtyStr}
                onChange={(e) => setInitialQtyStr(e.target.value)}
              />
              {showErrors && errors.initialQty ? (
                <span className="form-field__error">{errors.initialQty}</span>
              ) : (
                <span className="form-hint muted">Recorded as the opening stock movement.</span>
              )}
            </div>
          )}
        </section>

        <div className="form-actions form-actions--split">
          <div className="form-actions__group">
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
            {product && product.status === "archived" ? (
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => void publish()}
                disabled={acting}
              >
                {acting ? "Working…" : "Publish"}
              </button>
            ) : null}
            {product && product.status === "active" ? (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setToUnpublish(true)}
                disabled={acting}
              >
                Unpublish
              </button>
            ) : null}
          </div>
        </div>
      </form>

      {product ? (
        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">Stock movements</h2>
          </div>
          <ProductMovements productId={product.id} reloadToken={movementsToken} />
        </section>
      ) : null}

      {adjusting && product ? (
        <StockAdjustModal
          product={product}
          onClose={() => setAdjusting(false)}
          onAdjusted={onAdjusted}
        />
      ) : null}

      {toUnpublish ? (
        <ConfirmDialog
          title="Unpublish product"
          message="Unpublish this product? It will be removed from the storefront until re-published."
          confirmLabel="Unpublish"
          danger
          busy={acting}
          onConfirm={() => void confirmUnpublish()}
          onCancel={() => setToUnpublish(false)}
        />
      ) : null}
    </div>
  );
}
